import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, Send } from "lucide-react";
import { ViewHeader } from "@/components/admin/AdminShared";
import { EmailAccountsList } from "@/components/admin/EmailAccountsList";

const empty = { name: "", username: "", notes: "" };

export const EmailAccountsView = ({ onChanged }) => {
  const [domain, setDomain] = useState("");
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/admin/email-accounts").then((r) => setDomain(r.data.domain));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/admin/email-accounts", form);
      setCreated(data);
      setForm(empty);
      setRefreshKey((k) => k + 1);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-emails-view">
      <ViewHeader title="Create Email Accounts" subtitle={`Request a new @${domain} mailbox. The request is emailed to our email administrator.`} />

      <form onSubmit={submit} className="bg-white rounded-2xl border border-purple-100 p-6 space-y-4 mb-10">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ea-name">Person's full name</Label>
            <Input id="ea-name" data-testid="email-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Thandi Mokoena" className="mt-1 bg-white" />
          </div>
          <div>
            <Label htmlFor="ea-user">Email address</Label>
            <div className="mt-1 flex items-center rounded-md border border-input bg-white focus-within:ring-2 focus-within:ring-fuchsia-200">
              <input id="ea-user" data-testid="email-form-username" required pattern="[A-Za-z0-9._\-]{1,40}" title="Letters, numbers, dots, dashes or underscores" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.trim() })} placeholder="thandi" className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm outline-none" />
              <span className="px-3 text-sm text-muted-foreground border-l border-input">@{domain}</span>
            </div>
          </div>
        </div>
        <div>
          <Label htmlFor="ea-notes">Notes (optional)</Label>
          <Textarea id="ea-notes" data-testid="email-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. For the new sales assistant" className="mt-1 bg-white" />
        </div>
        <button type="submit" data-testid="email-form-submit" disabled={sending} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full brand-gradient text-white font-semibold disabled:opacity-50">
          <Send className="w-4 h-4" /> {sending ? "Sending request…" : "Submit"}
        </button>
      </form>

      <h3 className="font-semibold text-lg text-purple-950 mb-3">Current email accounts</h3>
      <EmailAccountsList refreshKey={refreshKey} />

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="email-request-success-dialog">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-2">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <DialogTitle>Email request sent</DialogTitle>
            <DialogDescription data-testid="email-request-success-message">
              <strong className="text-purple-950">{created?.address}</strong> has been requested. It will take 8 to 12 hours for the
              email to be active.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button data-testid="email-request-success-ok" onClick={() => setCreated(null)} className="px-6 py-2 rounded-full brand-gradient text-white font-semibold">
              OK
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
